import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categoryData, type CategoryItem } from '@/mocks/category';
import { categories } from '@/mocks/home';

const categoryNameMap: Record<string, string> = {
  dianying: '电影',
  dianshiju: '电视剧',
  zongyi: '综艺',
  dongman: '动漫',
};

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'dianying');
  const [items, setItems] = useState<CategoryItem[]>([]);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    if (slug && categoryData[slug]) {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveCategory(slug);
        setItems(categoryData[slug].items);
        setIsTransitioning(false);
      }, 200);
    }
  }, [slug]);

  const currentCategory = categoryData[activeCategory];

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <nav className="bg-background-50 border-b border-background-200/70" aria-label="面包屑导航">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-3">
            <ol className="flex items-center gap-2 text-xs text-foreground-500">
              <li><Link to="/" className="hover:text-primary-500 transition-colors">首页</Link></li>
              <li className="text-foreground-300">/</li>
              <li className="text-foreground-700 font-medium">{currentCategory?.name || '分类'}</li>
            </ol>
          </div>
        </nav>

        <section className="py-8 md:py-10 bg-background-100" aria-label="分类筛选">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex flex-wrap items-center gap-2 mb-6">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:text-foreground-950 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {currentCategory && (
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                  樱花视频{currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-500 max-w-2xl">
                  {currentCategory.description}
                </p>
              </div>
            )}
          </div>
        </section>

        <section className="py-8 md:py-10" aria-label="内容列表">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div
              className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5 transition-opacity duration-200 ${
                isTransitioning ? 'opacity-50' : 'opacity-100'
              }`}
            >
              {items.map((item) => (
                <Link
                  key={item.id}
                  to={`/category/${activeCategory}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-3">
                    <img
                      src={item.imageUrl}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                      loading="lazy"
                    />
                    <div className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5 rounded bg-background-950/60 backdrop-blur-sm">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-star-fill text-[10px] text-accent-400"></i>
                      </div>
                      <span className="text-[10px] text-background-50 font-medium">
                        {item.rating}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-950 truncate group-hover:text-primary-500 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1 line-clamp-2">
                    {item.description}
                  </p>
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    {item.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-[10px] font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                    <span className="text-[10px] text-foreground-400 ml-auto">{item.year}</span>
                  </div>
                </Link>
              ))}
            </div>

            {items.length === 0 && !isTransitioning && (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无相关内容</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}